import { useCallback, useContext, useEffect } from 'react';
import type { DisplayMode } from '@trinity/chat';
import { useLoadChatInstance } from '@trinity/chat-components/components/Chat.History/hooks/index.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier.js';
import {
  MessageGenerationCreateSessionContext,
  MessageGenerationStateContext,
} from '@/screens/MessageGenSession/Context';
import { useMessageGenChatReady } from '@/screens/MessageGenSession/useMessageGenChatReady';
import { ChatContextProviders } from './ChatContextProviders';
import { ChatInterfaceNative } from './ChatInterfaceNative';

const MESSAGE_GENERATION_AGENT_IDENTIFIER = 'message-generation-agent';

/**
 * Container that resolves the message-generation-agent chat context, injects
 * `messageGenSessionId` metadata, and renders `ChatInterfaceNative`
 * wrapped in `ChatContextProviders`.
 */
export function MessageGenChatInterface({
  onClose,
  headerActions,
  segmentIdentifier,
  segmentationRunId,
  mode,
}: {
  onClose?: () => void;
  headerActions?: React.ReactNode;
  segmentIdentifier?: string | null;
  segmentationRunId?: number | null;
  mode?: DisplayMode;
}): React.JSX.Element | null {
  return (
    <ChatContextProviders>
      <MessageGenChatInterfaceInner onClose={onClose} headerActions={headerActions} mode={mode} />
    </ChatContextProviders>
  );
}

function MessageGenChatInterfaceInner({
  onClose,
  headerActions,
  mode,
}: {
  onClose?: () => void;
  headerActions?: React.ReactNode;
  mode?: DisplayMode;
}): React.JSX.Element {
  const messageGenSession = useContext(MessageGenerationStateContext);
  const createSession = useContext(MessageGenerationCreateSessionContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const loadChatInstance = useLoadChatInstance();

  const messageGenChatIdentifier = messageGenSession?.chatInstanceIdentifier;

  useEffect(() => {
    if (!messageGenChatIdentifier) return;
    loadChatInstance(messageGenChatIdentifier);
  }, [loadChatInstance, messageGenChatIdentifier]);

  const isReady = useMessageGenChatReady();

  const onNewChat = useCallback(() => {
    if (!createSession || !brandIdentifier) return;
    createSession({
      brandIdentifier,
      sessionId: messageGenSession?.sessionId ?? undefined,
      initialState: {
        segmentIds: messageGenSession?.state.initialSegmentIds,
      },
      pageContext: messageGenSession?.pageContext ?? undefined,
    });
  }, [
    createSession,
    brandIdentifier,
    messageGenSession?.sessionId,
    messageGenSession?.state.initialSegmentIds,
    messageGenSession?.pageContext,
  ]);

  console.log('MessageGenChatInterfaceInner render', {
    isReady,
    messageGenChatIdentifier,
    mode,
  });

  return (
    <ChatInterfaceNative
      isReady={isReady}
      onClose={onClose}
      onNewChat={createSession && brandIdentifier ? onNewChat : null}
      headerActions={headerActions}
      mode={mode}
    />
  );
}
